import { createSelector } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import dayjs from "dayjs";
import { ICacheableFileResponse, IDownloadableFile, IValidationResult } from "lobbie";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getKey, isNewPastMinute } from "src/components/staff/file_downloads/utils";
import { DEFAULT_ERROR_MESSAGE } from "src/constants";
import { useAxiosGetter } from "src/hooks/useAxios";
import { IAppState } from "src/redux";
import {
    addDownloadableFile,
    addDownloadableFiles,
    setDownloadableFiles,
    setShowFileDownloads,
} from "src/redux/actions/fileDownloadsActions";
import { handleError, isFailedRequest, notify } from "src/utils";

interface IReduxFileDownloadsState {
    show: boolean;
    files: IDownloadableFile[];
}

const getFileDownloadsState = (state: IAppState): IReduxFileDownloadsState => {
    return state?.fileDownloads;
};

const showSelector = createSelector([getFileDownloadsState], (state: IReduxFileDownloadsState) => {
    return state.show;
});

const filesSelector = createSelector([getFileDownloadsState], (state: IReduxFileDownloadsState) => {
    return state.files;
});

export const useShowFileDownloads = (): [boolean, (show: boolean) => void] => {
    const dispatch = useDispatch();
    const isShow = useSelector(showSelector);

    const dispatchShowFiles = useCallback(
        (show: boolean) => dispatch(setShowFileDownloads(show)),
        [dispatch],
    );

    return [isShow, dispatchShowFiles];
};

export const usePreviouslyDownloadedFiles = (
    locationId: number | undefined,
): (() => Promise<IDownloadableFile[]>) => {
    const getter = useAxiosGetter();

    return useCallback(async () => {
        if (!locationId) {
            return [];
        }
        return getter(`/staff/files?locationId=${locationId}`)
            .then((response: AxiosResponse | void) => {
                const result =
                    response && (response.data as IDownloadableFile[] | IValidationResult);
                if (!result) {
                    // notify({
                    //     level: "error",
                    //     title: "Error getting files.",
                    //     message: DEFAULT_ERROR_MESSAGE,
                    // });
                    return [];
                } else if (isFailedRequest(result)) {
                    notify({
                        level: "error",
                        title: "Failed to get files.",
                        message: (result as IValidationResult).message || DEFAULT_ERROR_MESSAGE,
                    });
                    return [];
                } else {
                    return result as IDownloadableFile[];
                }
            })
            .catch((e) => {
                handleError(e);
                return [];
            });
    }, [locationId, getter]);
};

export const useDownloadableFiles = () => {
    const dispatch = useDispatch();
    const files = useSelector(filesSelector);

    const sorted = useMemo(
        () => [...files].sort((a, b) => b.lastUpdatedOnUnixUtc - a.lastUpdatedOnUnixUtc),
        [files],
    );
    const setFiles = useCallback(
        (newFiles: IDownloadableFile[]) => dispatch(setDownloadableFiles(newFiles)),
        [dispatch],
    );

    const addFile = useCallback(
        (newFile: IDownloadableFile) => dispatch(addDownloadableFile(newFile)),
        [dispatch],
    );

    const addFiles = useCallback(
        (newFiles: IDownloadableFile[]) => dispatch(addDownloadableFiles(newFiles)),
        [dispatch],
    );

    const cached = useMemo(
        () =>
            sorted.filter(
                (f) =>
                    f &&
                    typeof f === "object" &&
                    (f.s3ObjectPath || f.s3ObjectPaths) &&
                    !f.s3ObjectPath?.includes("app.drchrono.com") &&
                    !f.signedURL &&
                    dayjs(f.lastUpdatedOnUnix || f.lastUpdatedOnUnixUtc)
                        .add(5, "minutes")
                        .isAfter(dayjs()),
            ),
        [sorted],
    ) as ICacheableFileResponse[];

    const isCacheEmpty = useMemo(() => !cached?.length, [cached.length]);

    return { files: sorted, cached, isCacheEmpty, setFiles: setFiles, addFile, addFiles };
};

export const useOpenDownloadableFile = (file: IDownloadableFile) => {
    const { files, setFiles } = useDownloadableFiles();

    return useCallback(() => {
        if (file.signedURL) {
            if (file.newOnUnix) {
                const key = getKey(file);
                const index = files.findIndex((f) => getKey(f) === key);
                if (index !== -1) {
                    setFiles(
                        files.map((f, i) => {
                            if (i === index) {
                                return {
                                    ...f,
                                    newOnUnix: isNewPastMinute(file) ? file.newOnUnix : undefined,
                                    s3ObjectPath: "",
                                };
                            } else {
                                return f;
                            }
                        }),
                    );
                }
            }
            window.open(file.signedURL, "_blank");
        } else {
            // TODO: API CALL USING file.s3ObjectPath
        }
    }, [file, setFiles, files]);
};
